import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {useMemo, useRef, useState} from 'react'
import {useGetProvinces} from '@/services/useGetProvinces'
import {Province} from '@/types'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

interface InputSelectProvinceProps {
    label: string
    value?: Province | null
    onChange: (value: Province | null) => void
    placeholder?: string
    country?: string
    errorMessage?: string
    isPortal?: boolean
}

export const InputSelectProvince = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    isPortal
}: InputSelectProvinceProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputProvinceRef = useRef(null)
    const provincesQuery = useGetProvinces({limit: 110}, {enabled: isQueryEnabled})

    const selectedIndex = useMemo(
        () => provincesQuery.remappedData.findIndex(province => province.acronym == value?.acronym) ?? undefined,
        [provincesQuery.remappedData, value]
    )

    return (
        <ComboboxProvider setValue={provincesQuery.onSearch}>
            <SelectProvider
                value={value?.acronym ?? ''}
                setValue={value =>
                    onChange(provincesQuery.remappedData.find(province => value == province.acronym) ?? null)
                }
                setOpen={isOpen => {
                    provincesQuery.onResetSearch(inputProvinceRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.acronym ?? ''}
                        hasError={!!errorMessage}
                        displayValue={() => value?.acronym}
                    />
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                    <SelectComboboxPopover portal={!!isPortal} sameWidth>
                        <SelectComboboxInput ref={inputProvinceRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {provincesQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : provincesQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : provincesQuery.remappedData.length > 0 ? (
                                <SelectVirtualList
                                    key={selectedIndex ?? undefined}
                                    data={provincesQuery.remappedData}
                                    style={{height: 200}}
                                    totalCount={provincesQuery.remappedData.length}
                                    overscan={100}
                                    endReached={() => provincesQuery.hasNextPage && provincesQuery.fetchNextPage()}
                                    initialTopMostItemIndex={selectedIndex >= 0 ? selectedIndex : undefined}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.acronym}>
                                            {item.name}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
